import "@/components/styles/screen.scss";
import "./App.scss";
import "./index.css";

import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider } from "styled-components";

import { setTheme } from "@/components/whiteLabel/whiteLabel.service";
import { DEFAULT_THEME } from "@/components/whiteLabel/whiteLabel.settings";
import { getTheme } from "@/components/whiteLabel/whiteLabel.styled.service";
import { DefaultTheme } from "@/components/whiteLabel/whiteLabel.types";
import { listenForExoContext, listenForThemeContext } from "@/messenger/actions/exoContext";
// import { useUpdateMetadata } from "@/messenger/providers/Auth0Provider";
import { useAuth, useUpdateMetadata } from "@/messenger/providers/AuthProvider";
import runOneSignal from "@/messenger/providers/OneSignalProvider";
import { exoAppContextSelector, exoThemeSelector } from "@/messenger/selectors";

import { representativeSettingsSelectors } from "./ducks/entities/representativeSettings";
import { setDeviceType } from "./ducks/general/deviceType.duck";
import { setHasLogoAC } from "./ducks/general/hasLogo.duck";
import { deviceTypeSelector } from "./ducks/selectors/general";
import { useFetchUserMetadata } from "./hooks/useFetchUserMetadata";
import { useMessengerAuthorize } from "./hooks/useMessengerAuthorize";
import GlobalRouter from "./routes";
import { getDeviceType } from "./utils/deviceType";

const loadingStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
};

let savedContext = {};
let currentTheme = {};
let currentLocale = {};
export const getSavedContext = () => savedContext;
export const getCurrentTheme = () => currentTheme;
export const getCurrentLocale = () => currentLocale;




function App() {
    const [styledTheme, setStyledTheme] = useState<DefaultTheme | null>(null);
    const [isThemeLoaded, setThemeLoaded] = useState<boolean>(false);
    const authClient = useAuth();
    const authCredentials = useMessengerAuthorize(authClient);
    const [accessToken, userMetadata, setUserMetadata, metadataUrl] = useFetchUserMetadata();
    const [initialized, setInitialized] = useState(false);

    const deviceType = useSelector(deviceTypeSelector);
    const dispatch = useDispatch();

    const retrievedRepSettings = useSelector(representativeSettingsSelectors.selectAll);
    const representativeSettings = retrievedRepSettings.length > 0 && retrievedRepSettings[0];
    const locale = representativeSettings.locale ?? "en_CA";

    const themeContext = useSelector(exoThemeSelector);    
    const appContext = useSelector(exoAppContextSelector);    

    useEffect( () => {
        if (i18next.language !== locale.substring(0,2)) {
            i18next.changeLanguage(locale.substring(0,2));
        }
        currentLocale = locale;
        
    }, [locale]);

    const onMetadataChange = (metadata: any) => {
        setUserMetadata(metadata);

    };
    const updateMetadata = useUpdateMetadata(metadataUrl, accessToken, onMetadataChange, authClient.user, userMetadata);

    const updateDeviceType = () => {
        const currentDeviceType = getDeviceType();
        if (currentDeviceType !== deviceType) {
            dispatch(setDeviceType(currentDeviceType));
        }
    };
    
    useEffect(() => {
        dispatch(listenForExoContext());
        dispatch(listenForThemeContext());
    }, []);

    useEffect(() => {
        const delay = 300;
        let timeout = null;
        console.log("version: touchless/23.01.26");
        
        window.addEventListener("resize", () => {
            // clear the timeout
            if (timeout) {
                clearTimeout(timeout);
            }
            // start timing for event "completion"
            timeout = setTimeout(updateDeviceType, delay);
        });

        if (window !== undefined) {
            //@ts-ignore
            window.OneSignal = window.OneSignal || [];
        }

        dispatch(setDeviceType(getDeviceType()));

        return function updateDeviceTypeCleanup() {
            window.removeEventListener("resize", updateDeviceType);
        };
    }, []);

    useEffect(() => {
        async function themeSetter() {
            const savedTheme = localStorage.getItem("theme");
            
            const setDefault = (window && window.matchMedia && window.matchMedia("(prefers-color-scheme:dark)").matches) ? "dark" : false;
            if (!savedTheme && setDefault) {
                localStorage.setItem("theme",setDefault);
            }
            let useTheme = savedTheme ? themeContext?.[savedTheme] : appContext?.theme && setDefault !== "dark" ? appContext.theme : themeContext && setDefault ? themeContext[setDefault] : DEFAULT_THEME;
            if (savedTheme === "light" && appContext?.theme) { useTheme = appContext?.theme; }
            
            setStyledTheme(getTheme(useTheme));
            savedContext = appContext;
            currentTheme = useTheme;
            if (useTheme?.logoUrl !== null) {
                dispatch(setHasLogoAC(true));
            }
            
            //@ts-ignore
            if (appContext?.oneSignal?.appId && !initialized && window.OneSignal === []) {
                runOneSignal(appContext.oneSignal?.appId, initialized, setInitialized);
            }
            setTheme(useTheme);
            setThemeLoaded(true);
        }

        themeSetter();
    }, [themeContext, appContext]);

    if (!authClient.isReady && !isThemeLoaded) {
        return (
            <div style={loadingStyle}>
                <h2>Loading...</h2>
            </div>
        );
    }
    if (authClient.isReady && isThemeLoaded) {
        return (
            <ThemeProvider theme={styledTheme}>
                {/*isTestEnv && <LanguageToggle />*/}
                <GlobalRouter
                    userMetadata={userMetadata}
                    updateMetadata={updateMetadata}
                    authCredentials={authCredentials}
                />
            </ThemeProvider>
        );
    }

    return null;
}

export default App;
